import * as Yup from 'yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import { RouterLink } from 'src/routes/components';
import FormControlLabel from '@mui/material/FormControlLabel';
import { fData } from 'src/utils/format-number';
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { IUserItem } from 'src/types/user';
import { countries } from 'src/assets/data';
import { useGetRoles } from 'src/api/admin';
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, {
  RHFSwitch,
  RHFTextField,
  RHFUploadAvatar,
  RHFAutocomplete,
} from 'src/components/hook-form';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { useBoolean } from 'src/hooks/use-boolean';
import { Container } from '@mui/system';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs/custom-breadcrumbs';
import { useSettingsContext } from 'src/components/settings';
import axiosInstance, { endpoints } from 'src/utils/axios';

type Props = {
  id?: any;
};
type IRoles = {
  value: number;
  label: string;
};
export default function UserNewCreateForm({ id }: Props) {
  const settings = useSettingsContext();
  const [showPassword, setShowPassword] = useState(false);
  const [Password, setPassword] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { roles, productsLoading, total, productsEmpty } = useGetRoles();
  const roleslist = roles;
  const password = useBoolean();
  const router = useRouter();
  const NewUserSchema = Yup.object().shape({
    username: Yup.string().required('Name is required').trim(),
    email: Yup.string()
      .required('Email is required')
      .email('Email must be a valid email address')
      .trim(),
    phone: Yup.string().required('Phone number is required').trim(),
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters')
      .test(
        'no-match',
        'New password must be different than old password',
        (value, { parent }) => value !== parent.oldPassword
      )
      .trim(),
    confirmpassword: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .trim(),
    role_id: Yup.mixed<any>().required('Roles is required'),
    file: Yup.mixed<any>().nullable().required('Image is required'),
  });
  const defaultValues = {
    username: '',
    password: '',
    email: '',
    phone: '',
    confirmpassword: '',
    // Daterange: '',
    role_id: null,
    device_token: 'realdeviceid',
    file: null,
  };
  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data: any) => {
    const formData = new FormData();

    const details = {
      email: data.email || '',
      username: data.username || '',
      password: data.password || '',
      phone: data.phone || '',
      confirm_password: data.confirmpassword || '',
      role_id: data.role_id.value || '',
      device_token: 'realdeviceid',
    };
    const file = data.file;
    formData.append('info', JSON.stringify(details));

    const selectedParentCategory = roles.find((item: any) => item.value === data.role_id);
    if (selectedParentCategory) {
      formData.append(
        'info',
        JSON.stringify({ ...details, role_id: selectedParentCategory.label })
      );
    }

    if (file) {
      formData.append('file', file);
    }
    const token = localStorage.getItem('accessToken');

    try {
      await axiosInstance.post(endpoints.admin.register, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      enqueueSnackbar('Registered Successfully!');
      router.push(paths.dashboard.accessrole.adminUser);
    } catch (error) {
      if (error.error) {
        enqueueSnackbar(error.error, { variant: 'error' });
      } else {
        enqueueSnackbar('Something went wrong. Please try again later', { variant: 'error' });
      }
    }
  });
  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        setValue('file', newFile, { shouldValidate: true });
      }
    },
    [setValue]
  );
  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Create New Admin user"
        links={[{}]}
        sx={{
          // mb: { xs: 1, md: 2 },
          mb: 0,
        }}
      />
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Grid container spacing={3}>
          <Grid xs={12} md={8}>
            <Card sx={{ p: 3, height: '100%' }}>
              <Box
                rowGap={3}
                columnGap={2}
                display="grid"
                gridTemplateColumns={{
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                }}
              >
                <RHFTextField name="username" label="Username" />
                <RHFTextField name="email" label="Email Address" />
                {/* <RHFTextField name="password" label="Password" />
                <RHFTextField name="confirmpassword" label="Confirm Password" /> */}

                <RHFTextField
                  name="password"
                  label="Password"
                  type={Password ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => setPassword((prevShowPassword) => !prevShowPassword)}
                        >
                          <Iconify icon={Password ? 'eva:eye-off-outline' : 'eva:eye-outline'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  autoComplete="new-password"
                />
                <RHFTextField
                  name="confirmpassword"
                  label="Confirm Password"
                  type={showPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => setShowPassword((prevShowPassword) => !prevShowPassword)}
                        >
                          <Iconify
                            icon={showPassword ? 'eva:eye-off-outline' : 'eva:eye-outline'}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  autoComplete="new-password"
                />

                <RHFTextField name="phone" label="Phone Number" />
                <RHFAutocomplete
                  name="role_id"
                  label="Roles"
                  options={roles}
                  getOptionLabel={(option: IRoles | string) => (option as IRoles).label}
                  isOptionEqualToValue={(option, valu) =>
                    valu === undefined || option.value === valu.value
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.value}>
                      {option.label}
                    </li>
                  )}
                />
              </Box>
              <br />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  size="small"
                  loading={isSubmitting}
                  sx={{ backgroundColor: '#5da5da' }}
                >
                  {id ? 'Update User ' : 'Create User'}
                </LoadingButton>
                &nbsp;
                <LoadingButton
                  sx={{ backgroundColor: '#5da5da' }}
                  component={RouterLink}
                  href={paths.dashboard.accessrole.adminUser}
                  variant="contained"
                  size="small"
                >
                  Back
                </LoadingButton>
              </Box>
            </Card>
          </Grid>
          <Grid xs={12} md={4}>
            <Card sx={{ pt: 4, pb: 5, px: 3, height: '100%' }}>
              <Box sx={{ mb: 5 }}>
                <RHFUploadAvatar
                  name="file"
                  maxSize={3145728}
                  onDrop={handleDrop}
                  helperText={
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 3,
                        mx: 'auto',
                        display: 'block',
                        textAlign: 'center',
                        color: 'text.disabled',
                      }}
                    >
                      Allowed *.jpeg, *.jpg, *.png, *.gif
                      <br /> max size of {fData(3145728)}
                    </Typography>
                  }
                />
              </Box>
            </Card>
          </Grid>
        </Grid>
      </FormProvider>
    </Container>
  );
}
